import { GetStaticPaths, GetStaticProps } from "next";
import { FunctionComponent } from "react";
import {
  CopyProps,
  UtilityCopy,
  UtilityCopyProps,
} from "@bluebottlecoffee/design-system";
import { BaseProps, filters } from "../../../lib/sanity/shared";
import {
  getDefaultStaticPaths,
  getDefaultStaticProps,
} from "../../../lib/utils";
import { LayoutWrapper } from "../../../components/LayoutWrapper";
import { PrivacyPage as PrivacyPageSchema } from "../../../lib/sanity-schema";
import sanityClient from "../../../lib/sanity-client";
import renderModule from "../../../lib/render-module";
import {
  buildTranslationGroupQuery,
  toCopyObject,
  TranslationGroupSanityType,
} from "../../../lib/sanity/translation-group-queries";
import { toUtilityCopyProps } from "../../../lib/transformers/utility-copy";

interface PageData extends BaseProps {
  privacyPage: PrivacyPageSchema;
  utilityTranslations: CopyProps;
}

export const getStaticProps: GetStaticProps<PageData> = async (context) => {
  const { props: defaultProps } = await getDefaultStaticProps(context);

  const { lang } = defaultProps;

  const query = buildTranslationGroupQuery(lang, "UtilityPage");

  const utilityTranslations = await sanityClient()
    .fetch<TranslationGroupSanityType>(query)
    .then((translations: TranslationGroupSanityType) =>
      toCopyObject<CopyProps>(lang, translations),
    );

  const privacyPage = await sanityClient().fetch<PrivacyPageSchema>(`*[
    ${filters.whereType("privacyPage")} &&
    ${filters.excludeDrafts}
  ][0]`);

  return {
    props: {
      privacyPage,
      utilityTranslations,
      ...defaultProps,
    },
  };
};

export const getStaticPaths: GetStaticPaths = getDefaultStaticPaths;

function validate(data: PrivacyPageSchema) {
  if (!data) {
    throw Error("Page data not found, please ensure one has been created.");
  }
  if (!data.title) {
    throw Error("Privacy Page must have a title.");
  }
}

const PrivacyPage: FunctionComponent<PageData> = ({
  privacyPage,
  utilityTranslations,
  ...layoutWrapperProps
}) => {
  validate(privacyPage);

  const { region, lang } = layoutWrapperProps;

  const utilityCopyProps: UtilityCopyProps = {
    ...toUtilityCopyProps(privacyPage.copy, region, lang),
    copyProps: utilityTranslations,
  };

  return (
    <LayoutWrapper
      {...{
        ...layoutWrapperProps,
        pageTitle: privacyPage.title[lang],
        metaTags: privacyPage.metaTags,
      }}
    >
      {privacyPage.copy && <UtilityCopy {...utilityCopyProps} />}
    </LayoutWrapper>
  );
};

export default PrivacyPage;
